import { ContactLink } from '@/components/contact';

/**
 * TermsOfServiceContent Component
 *
 * Reusable terms of service content for:
 * - Modal display during signup (scroll-to-bottom requirement)
 * - Standalone page at /terms
 *
 * WP-CONTACT-1: Updated to use ContactLink instead of mailto
 */
export function TermsOfServiceContent() {
  const lastUpdated = 'February 11, 2026';

  return (
    <>
      {/* Last Updated */}
      <div className="text-gray-600 mb-8">
        Last updated: {lastUpdated}
      </div>

      {/* Introduction */}
      <section className="mb-10">
        <p className="text-gray-700 leading-relaxed mb-4">
          Welcome to FlowIO. These Terms of Service ("Terms") govern your access to and use of
          the FlowIO website, applications, and services (collectively, the "Service"). Please
          read these Terms carefully before using our Service.
        </p>
        <p className="text-gray-700 leading-relaxed">
          By accessing or using the Service, you agree to be bound by these Terms. If you
          disagree with any part of these Terms, you may not access the Service.
        </p>
      </section>

      {/* Section 1: Service Description */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          1. Service Description
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          FlowIO is a personal finance application available on web and mobile platforms
          that provides:
        </p>
        <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
          <li>Automated aggregation of financial data from your bank accounts and credit cards</li>
          <li>Visual representation of your money flow through Sankey-style diagrams</li>
          <li>Transaction categorization and analysis tools using machine learning</li>
          <li>Household financial management with multi-user support</li>
          <li>Budgeting and financial planning features</li>
          <li>Document upload and processing for bank statements</li>
          <li>Data import from other personal finance applications</li>
          <li>Mobile applications for iOS and Android</li>
        </ul>
        <p className="text-gray-700 leading-relaxed mt-4">
          The Service is designed for personal financial management purposes and does not
          provide financial, investment, tax, or legal advice.
        </p>
      </section>

      {/* Section 2: Eligibility */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          2. Eligibility
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          To use the Service, you must:
        </p>
        <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
          <li>Be at least 18 years of age</li>
          <li>Have the legal capacity to enter into binding agreements</li>
          <li>Provide accurate and complete registration information</li>
          <li>Not be prohibited from using the Service under applicable laws</li>
        </ul>
      </section>

      {/* Section 3: Account Terms */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          3. Account Terms
        </h2>
        <h3 className="text-lg font-medium text-gray-800 mb-3">
          3.1 Account Creation
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          You must create an account to access the Service. When creating an account, you
          agree to provide accurate, current, and complete information.
        </p>

        <h3 className="text-lg font-medium text-gray-800 mb-3">
          3.2 Household Accounts
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          FlowIO supports household accounts where multiple users can share access to
          financial data. The primary account holder is responsible for managing household
          membership and all activity within the household account.
        </p>

        <h3 className="text-lg font-medium text-gray-800 mb-3">
          3.3 Account Security
        </h3>
        <p className="text-gray-700 leading-relaxed">
          You are responsible for all activities that occur under your account. Notify us
          immediately of any unauthorized use. We recommend enabling multi-factor
          authentication (MFA).
        </p>
      </section>

      {/* Section 4: User Responsibilities */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          4. User Responsibilities
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          When using the Service, you agree to:
        </p>
        <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
          <li>Use the Service only for lawful purposes</li>
          <li>Provide accurate credentials when connecting financial accounts</li>
          <li>Keep your account information secure</li>
          <li>Not attempt to gain unauthorized access to any part of the Service</li>
          <li>Not interfere with or disrupt the Service</li>
          <li>Not transmit any malicious code</li>
          <li>Comply with all applicable laws and regulations</li>
        </ul>
      </section>

      {/* Section 5: Data Usage and Privacy */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          5. Data Usage and Privacy
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Your privacy is important to us. Our collection and use of your personal and
          financial information is governed by our Privacy Policy, which is incorporated
          into these Terms by reference.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          By using the Service, you consent to:
        </p>
        <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
          <li>Collection and processing of your financial data</li>
          <li>Secure storage of your account credentials</li>
          <li>Use of aggregated, anonymized data for analytics</li>
          <li>Automated transaction categorization using machine learning</li>
          <li>Processing of documents you upload</li>
        </ul>

        <h3 className="text-lg font-medium text-gray-800 mb-3 mt-6">
          5.1 Uploaded Content
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          When you upload documents, you represent that you have the right to upload and
          share them, and they do not contain malicious content. Original documents are
          encrypted and automatically deleted within 30 days of processing.
        </p>
      </section>

      {/* Section 6: Third-Party Services */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          6. Third-Party Services
        </h2>
        <h3 className="text-lg font-medium text-gray-800 mb-3">
          6.1 Financial Data Aggregation
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          FlowIO uses third-party services to connect to your financial institutions:
        </p>
        <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
          <li>
            <strong>Plaid Inc.:</strong> By connecting accounts through Plaid, you authorize
            Plaid to access your financial information on your behalf.
          </li>
          <li>
            <strong>Akoya:</strong> Alternative aggregation for participating banks.
          </li>
        </ul>

        <h3 className="text-lg font-medium text-gray-800 mb-3">
          6.2 Payment Processing
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          FlowIO uses Stripe, Inc. for subscription billing. Your payment information is
          handled directly by Stripe. We do not store your complete credit card information.
        </p>

        <h3 className="text-lg font-medium text-gray-800 mb-3">
          6.3 Third-Party Disclaimer
        </h3>
        <p className="text-gray-700 leading-relaxed">
          We are not responsible for the practices or policies of third-party services.
          Your interactions with them are governed by their respective terms.
        </p>
      </section>

      {/* Section 7: Subscription and Billing */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          7. Subscription and Billing
        </h2>
        <h3 className="text-lg font-medium text-gray-800 mb-3">
          7.1 Subscription Plans
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          FlowIO offers various subscription plans. We reserve the right to modify pricing
          with 30 days' notice to existing subscribers.
        </p>

        <h3 className="text-lg font-medium text-gray-800 mb-3">
          7.2 Billing Cycle
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          Subscriptions are billed in advance on a recurring basis. Your subscription will
          automatically renew unless cancelled before the renewal date.
        </p>

        <h3 className="text-lg font-medium text-gray-800 mb-3">
          7.3 Cancellation and Refunds
        </h3>
        <p className="text-gray-700 leading-relaxed">
          You may cancel your subscription at any time through your account settings.
          Cancellation takes effect at the end of the current billing period.
        </p>
      </section>

      {/* Section 8: Intellectual Property */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          8. Intellectual Property
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          The Service and its original content, features, and functionality are owned by
          FlowIO and are protected by intellectual property laws.
        </p>
        <p className="text-gray-700 leading-relaxed">
          You retain ownership of your financial data. By using the Service, you grant us
          a limited license to use, process, and display your data solely for providing
          the Service.
        </p>
      </section>

      {/* Section 9: Limitation of Liability */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          9. Limitation of Liability
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4 uppercase text-sm font-medium">
          To the maximum extent permitted by applicable law:
        </p>
        <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
          <li>The Service is provided "as is" without warranties of any kind</li>
          <li>We do not warrant that the Service will be uninterrupted or error-free</li>
          <li>We are not responsible for data accuracy from financial institutions</li>
          <li>We are not liable for decisions you make based on Service information</li>
        </ul>
        <p className="text-gray-700 leading-relaxed">
          In no event shall FlowIO be liable for any indirect, incidental, special,
          consequential, or punitive damages arising from your use of the Service.
        </p>
      </section>

      {/* Section 10: Indemnification */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          10. Indemnification
        </h2>
        <p className="text-gray-700 leading-relaxed">
          You agree to defend, indemnify, and hold harmless FlowIO from any claims,
          damages, or expenses arising from: (a) your use of the Service; (b) your
          violation of these Terms; (c) your violation of any third-party rights.
        </p>
      </section>

      {/* Section 11: Termination */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          11. Termination
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          We may terminate or suspend your account immediately, without prior notice,
          for any reason, including if you breach these Terms.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          Upon termination:
        </p>
        <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
          <li>Your right to use the Service will immediately cease</li>
          <li>We may delete your account and all associated data</li>
          <li>You may request export of your data before deletion</li>
        </ul>
      </section>

      {/* Section 12: Changes to Terms */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          12. Changes to Terms
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          We reserve the right to modify these Terms at any time. For material changes,
          we will provide at least 30 days' notice. By continuing to use the Service after
          changes become effective, you agree to be bound by the revised terms.
        </p>
      </section>

      {/* Section 13: Governing Law */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          13. Governing Law and Dispute Resolution
        </h2>
        <h3 className="text-lg font-medium text-gray-800 mb-3">
          13.1 Governing Law
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          These Terms shall be governed by the laws of the State of Delaware, United States.
        </p>

        <h3 className="text-lg font-medium text-gray-800 mb-3">
          13.2 Binding Arbitration
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          <strong>IMPORTANT: THIS AFFECTS YOUR LEGAL RIGHTS.</strong>
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          You and FlowIO agree that disputes will be resolved through binding individual
          arbitration rather than in court, except for intellectual property matters.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          <strong>No Class Actions:</strong> Disputes may only be brought individually,
          not as part of class or representative proceedings.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          <strong>Opt-Out:</strong> You may opt out by sending written notice to{' '}
          <ContactLink
            topic="legal"
            context="TermsOfServiceContent - Arbitration opt-out"
            className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
          >
            our legal team
          </ContactLink>{' '}
          within 30 days of creating your account.
        </p>
      </section>

      {/* Section 14: Miscellaneous */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          14. Miscellaneous
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          <strong>Entire Agreement:</strong> These Terms constitute the entire agreement
          between you and FlowIO regarding the Service.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          <strong>Severability:</strong> If any provision is held invalid, the remaining
          provisions will remain in effect.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          <strong>Waiver:</strong> Our failure to enforce any right will not be considered
          a waiver.
        </p>
        <p className="text-gray-700 leading-relaxed">
          <strong>Assignment:</strong> You may not assign these Terms without our consent.
          We may assign our rights without restriction.
        </p>
      </section>

      {/* Section 15: Contact */}
      <section className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          15. Contact Information
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          If you have any questions about these Terms of Service, please contact us at:
        </p>
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <p className="text-gray-700 font-medium">FlowIO Support</p>
          <p className="text-gray-700">
            <ContactLink
              topic="support"
              context="TermsOfServiceContent - Contact section"
              className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
            >
              Contact us
            </ContactLink>
          </p>
        </div>
      </section>
    </>
  );
}

export default TermsOfServiceContent;
